import { FC, useEffect, useState } from 'react'
import { ColorOptions, FontSizeOptions, FontWeightOptions, SpaceOptions } from '../../theme/UiThemeProvider'
import { Box } from '../BoxNew'
import { Icon } from '../Icon'
import { Spacer } from '../Spacer'
import { Text } from '../Text'

const getIconFont = (isChecked?: boolean) => {
  if (isChecked) {
    return 'RADIOBUTTON_ON'
  }
  return 'RADIOBUTTON_OFF'
}

export interface RadioButtonProps {
  textLabel?: string
  textSize?: FontSizeOptions | number
  color?: ColorOptions
  onChange?: (isChecked: string) => void | Promise<void>
  value?: string
  checked?: boolean
  loading?: boolean
  testID?: string
  size?: FontSizeOptions | number
  disabled?: boolean
  fontFamily?: string
  textColor?: ColorOptions
  seperatorWidth?: number
  seperatorColor?: ColorOptions
  minItemHeight?: number
  fontWeight?: FontWeightOptions
  textPadding?: number
  padding?: number
  opacity?: number
  hoverColor?: ColorOptions
  unCheckedColor?: ColorOptions
  disabledColor?: ColorOptions
}

export const RadioButton: FC<RadioButtonProps> = ({
  textLabel = '',
  textSize = 'FONT_SIZE_LARGE',
  color = 'PRIMARY',
  onChange: onPress,
  checked,
  value,
  testID,
  size = 'FONT_SIZE_EXTRA_LARGE',
  disabled = false,
  fontFamily,
  textColor = 'BLACK',
  seperatorWidth,
  seperatorColor,
  minItemHeight = 48,
  fontWeight,
  textPadding,
  padding,
  opacity,
  hoverColor = 'BG_SECONDARY_BUTTON_HOVER',
  unCheckedColor = 'DARK_GREY',
  disabledColor,
}) => {
  let isCheck = checked
  if (value !== undefined) {
    isCheck = value === 'true'
  }

  const onSelected = async () => {
    if (onPress && !disabled) {
      onPress(isCheck ? 'false' : 'true')
    }
  }
  return (
    <Box
      flexDirection="row"
      hoverColor={disabled ? undefined : hoverColor}
      padding={padding ?? 8}
      paddingLeft={textPadding}
      alignItems="center"
      onPress={onSelected}
      underlayColor={disabled ? 'TRANSPARENT' : 'LIGHTEST_GREY_2'}
      borderBottomWidth={seperatorWidth}
      borderBottomColor={seperatorColor}
      minHeight={minItemHeight}>
      <Box underlayColor="LIGHTEST_GREY_2" disabled={disabled} onPress={onSelected} testID={testID}>
        <Icon
          id={getIconFont(isCheck)}
          color={isCheck ? color : disabled && disabledColor ? disabledColor : unCheckedColor} //'DISABLED_GREY'
          size={size}
        />
      </Box>
      {textLabel !== '' && (
        <>
          <Spacer direction="horizontal" size={8} />
          <Text
            type="h2"
            title={textLabel}
            fontSize={textSize}
            color={disabled && disabledColor ? disabledColor : textColor} // 'DISABLED_GREY'
            fontFamily={fontFamily}
            fontWeight={fontWeight}
            opacity={opacity}
          />
        </>
      )}
    </Box>
  )
}

export interface RadioButtonGroupProps {
  color?: ColorOptions
  onChange?: (isChecked: string) => void | Promise<void>
  value?: string
  loading?: boolean
  data: string[]
  direction?: 'vertical' | 'horizontal'
  testID?: string
  error?: string
  touched?: boolean
  disabled?: boolean
  disabledColor?: ColorOptions
  hide?: boolean
  size?: FontSizeOptions | number
  textSize?: FontSizeOptions | number
  fontFamily?: string
  textColor?: ColorOptions
  seperatorWidth?: number
  seperatorColor?: ColorOptions
  minItemHeight?: number
  fontWeight?: FontWeightOptions
  textPadding?: number
  horizontalGap?: SpaceOptions | number
  checked?: boolean
  disabledIndividualItemIndex?: number | undefined
  callOnChangeOnLoad?: boolean
}

export const RadioButtonGroup: FC<RadioButtonGroupProps> = (props) => {
  const {
    value,
    data,
    direction = 'vertical',
    color,
    onChange,
    testID,
    error,
    touched,
    disabled = false,
    hide,
    size,
    textSize,
    fontFamily,
    textColor,
    seperatorWidth = 0,
    seperatorColor = 'BORDER_COLOR',
    minItemHeight,
    fontWeight = '500',
    textPadding = 8,
    horizontalGap = 'MEDIUM',
    checked = false,
    disabledIndividualItemIndex,
    callOnChangeOnLoad = true,
    disabledColor,
  } = props
  const [selected, setSelected] = useState(value)
  const isError = touched && !!error

  useEffect(() => {
    setSelected(value)
    if (onChange && !!value) {
      if (callOnChangeOnLoad) {
        onChange(value)
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value])

  if (hide) {
    return null
  }

  const selectItem = (label: string) => {
    setSelected(label)
    if (onChange) {
      onChange(label)
    }
  }
  const contents = data.map((label, index) => (
    <RadioButton
      testID={`${testID}${index}`}
      key={index.toString()}
      color={disabled && disabledColor ? disabledColor : color}
      textLabel={label}
      checked={selected === label || checked}
      onChange={() => {
        selectItem(label)
      }}
      disabled={disabled}
      disabledColor={disabledColor}
      size={size}
      textSize={textSize}
      fontFamily={fontFamily}
      textColor={disabled && disabledColor ? disabledColor : textColor}
      seperatorWidth={seperatorWidth}
      seperatorColor={seperatorColor}
      minItemHeight={minItemHeight}
      fontWeight={fontWeight}
      textPadding={textPadding}
      opacity={disabledIndividualItemIndex === index ? 0.3 : 1}
      hoverColor={!!props.hoverColor}
    />
  ))
  return (
    <Box>
      <Box
        gap={direction === 'vertical' ? undefined : horizontalGap}
        flexDirection={direction === 'vertical' ? 'column' : 'row'}>
        {contents}
      </Box>
      {isError ? (
        <>
          <Spacer direction="vertical" size={8} />
          <Text type="h4" color="ERROR">
            {error}
          </Text>
        </>
      ) : null}
    </Box>
  )
}
